import { RotatingCaret } from "@/components/generalUI/RotatingCaret";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import {
  CheckboxButton,
  colors2,
  DropdownSingle,
  TextInput,
  TypoV2ClassesProvider,
  UI_BODY_SEMIBOLD_SM_DF,
  UI_HELPTEXT,
} from "oolib";
import { DisplayIcon } from "oolib/dist/utils/comps/DisplayIcon";
import {
  globalInputElemBaseBorderStyling_v2,
  globalInputElemDisabledStyling_v2,
  globalInputElemFocused_v2,
  globalInputElemHover_v2,
  globalInputElemPadding_v2,
} from "oolib/dist/v2/themes/globalStyles";
import React, { Fragment, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import {
  Lightbox,
  LightboxTrigger,
  LightboxContent,
} from "@/components/UI/lightbox";
import FieldSelectorListItem from "./comps/FieldSelectorListItem";
import { SegmentedControl } from "@/components/UI/SegmentedControl";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";
import { WarningNotice } from "@/components/infoBoxes/WarningNotice";
import { fieldTypeTooltips, fieldTypeTooltipConfig } from "@/_self_managed/DirectDataImport/tooltipConfig";
import {
  LEGACY_RICH_TEXT_COMPS,
  deriveFieldTypeKey,
  isAiAnalysisOnForOutlineItem,
} from "@/_self_managed/DirectDataImport/textCompDisplay";

const SelectWrapperOverride = styled.div`
  width: 100%;

  .StyledDropdownRefWrapper_styleOverrideHandle {
  }

  /* Target the StyledSelectWrapper inside */
  .StyledSelectWrapper_styleOverrideHandle {
    &::before {
      background-color: unset;
      border: none;
      /* whatever ::before styles you want */
    }
  }
`;

/**
 * The trigger button. Exported because the trigger markup lives in the parent
 * (passed in as a render-prop child) while the styling stays here alongside the
 * `.StyledFieldSelectorButton_styleOverrideHandle` class that consumers target.
 */
export const StyledFieldSelectorButton = styled.button`
  width: 100%;
  height: 100%;
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  border: none;
  background: none;
  cursor: pointer;

  ${({ resetStyles }) =>
    !resetStyles &&
    css`
      position: relative;
      min-height: 4rem;
      isolation: isolate; /* needed for z-index context to be confined to this element instead of the document body */

      ${globalInputElemBaseBorderStyling_v2};
      ${globalInputElemHover_v2};
      ${globalInputElemDisabledStyling_v2}
      ${globalInputElemPadding_v2};
      ${globalInputElemFocused_v2}
    `}
`;

// The panel is a fixed 300px wide. Staying inside the viewport is handled by the
// shared Lightbox's floating-ui `shift` middleware (same 8px gutter as before).
const LIGHTBOX_WIDTH = 300;
// Width a full-bleed control gets inside the panel: the panel minus a
// StyledLightboxSection's 1.2rem of padding on either side.
const FIELD_WIDTH = LIGHTBOX_WIDTH - 24;

const StyledPanelSections = styled.div`
  /**
   * oolib sizes a dropdown's option lightbox to its content (max-content, capped
   * at 36rem), which is wider than this panel. Pinning the list to the field
   * width makes the wrapper oolib measures around it follow suit, so the
   * lightbox lines up with its trigger instead of spilling out of the panel.
   */
  #StyledOptionsWrapper {
    min-width: 0;
    max-width: ${FIELD_WIDTH}px;
  }

  & > *:not(:empty) ~ *:not(:empty) {
    border-top: 0.1rem solid ${colors2.grey10};
  }

  & > *:empty {
    display: none;
  }
`;

const StyledLightboxSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  padding: 1.2rem;
`;

// Toggle sits flush under the section above it (no bottom padding) so the
// following config section reads as part of the same group.
const StyledSegmentedToggleWrapper = styled.div`
  padding: 1.2rem 1.2rem 0;
`;

const StyledTextInput = styled(TextInput)`
  max-width: 45px;
`;

/**
 * Blocks the rest of the page while a column is set to "Existing Field" with no
 * field chosen yet: the choice has to be made (or the toggle flipped back to
 * "New Field") before anything else can be touched. Transparent — the panel is
 * still the thing being looked at — and sits under the panel's own z-index so
 * the panel and its dropdowns stay live.
 */
const StyledMappingBlockingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 999999998;
`;

const StyledErrorMessage = styled.div`
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  border-radius: 0.6rem;
  color: ${colors2.redText};
  background: ${colors2.redBG};
`;

// Low-confidence auto-detection indicator: a yellow dot in the column header
// nudging the user to verify the detected field type against the table data.
// Cleared for good once the picker is opened (see typeConfidenceAcked).
// Exported because the trigger markup (which renders it) lives in the parent
// via the render-prop, same as StyledFieldSelectorButton.
export const StyledConfidenceDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${colors2.yellow};
  flex-shrink: 0;
`;

const separationFormatOptions = [
  { icon: "Comma", display: "Comma", value: "comma", splitCharacter: "," },
  {
    icon: "Semicolon",
    display: "Semicolon",
    value: "semicolon",
    splitCharacter: ";",
  },
  { icon: "Slash", display: "Slash", value: "slash", splitCharacter: "/" },
  {
    icon: "PencilSimpleLine",
    display: "Custom",
    value: "custom",
    splitCharacter: null,
  },
  {
    icon: "X",
    display: "No Separation",
    value: "none",
    splitCharacter: "",
    isGroupSeparator: true,
  },
];

const getSeparationFormatOption = (arg) => {
  if (arg === undefined || arg === "")
    return separationFormatOptions.find((option) => option.value === "none");

  const predefinedOptions = separationFormatOptions.filter(
    (option) => option.splitCharacter !== null
  );

  if (predefinedOptions.some((option) => option.splitCharacter === arg)) {
    const matchingOption = predefinedOptions.find(
      (option) => option.splitCharacter === arg
    );
    return (
      matchingOption ||
      separationFormatOptions.find((option) => option.value === "custom")
    );
  }

  return separationFormatOptions.find((option) => option.value === arg);
};

/**
 * The panel's controls, for the `controlsToShow` prop to select between. Each
 * still has its own render conditions on top (e.g. `dateFormat` only shows for
 * date columns) — `controlsToShow` only ever narrows further, it never forces a
 * control on.
 */
export const CONTROL_SELECTOR_TYPES = [
  "dontImport", // "Don't import this column" toggle + its divider
  "mappingMode", // "New Field" / "Existing Field" toggle (reimport only)
  "changeFieldComp", // the "Field type" dropdown
  "separationFormat", // multi-select separator config
  "dateFormat", // "Date format" dropdown (date columns only)
  "tagType", // tag category dropdown (tag inputs only)
  "aiAnalysis", // "Include in AI Analysis" toggle (text columns only)
];

/**
 * Separator config for multi-select fields: the "pick a separator" reminder
 * plus the format dropdown (+ custom-character input). Shared by the new-field
 * and the map-to-existing-field branches so the JSX isn't duplicated.
 */
const MultiSelectSeparationConfig = ({
  separationFormat,
  separationFormatValue,
  disabled,
  onChange,
}) => (
  <Fragment>
    {/* Remind the user to pick a separator while it's still on "No Separation" —
        otherwise each cell's combined values import as a single option. */}
    {separationFormat === "none" && (
      <WarningNotice style={{ marginTop: "0.4rem" }}>
        Don't forget to select a separation format if your values contain
        multiple options per cell.
      </WarningNotice>
    )}
    <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: "0.4rem" }}>
        <DropdownSingle
          label={SM_UIContent.SM_DirectDataImport.dropdowns.separationFormat}
          placeholder={
            SM_UIContent.SM_DirectDataImport.dropdowns.separationFormatPlaceholder
          }
          disabled={disabled}
          saveValueAsString={true}
          popOutOfOverflowHiddenParent
          S
          value={separationFormat}
          options={[
            {
              groupLabel: "",
              options: separationFormatOptions
                .filter((op) => !op.isGroupSeparator)
                .map((op) => ({
                  icon: op.icon,
                  display: op.display,
                  value: op.value,
                })),
            },
            {
              groupLabel: "",
              options: separationFormatOptions
                .filter((op) => op.isGroupSeparator)
                .map((op) => ({
                  icon: op.icon,
                  display: op.display,
                  value: op.value,
                })),
            },
          ]}
          onChange={(_, v) => {
            const selectedOpt = getSeparationFormatOption(v);
            onChange(null, {
              separationFormat: v,
              // For "custom", keep empty. For others, use their splitCharacter
              separationFormatValue:
                v === "custom" ? "" : selectedOpt?.splitCharacter || "",
            });
          }}
          style={{ flexGrow: 1 }}
        />
        {separationFormat === "custom" && (
          <StyledTextInput
            S
            placeholder=""
            value={separationFormatValue}
            onChange={(_, v) => {
              onChange(null, { separationFormatValue: v });
            }}
          />
        )}
      </div>
      {separationFormat === "custom" && !separationFormatValue && (
        <StyledErrorMessage>
          <DisplayIcon
            size={12}
            icon="WarningDiamond"
            weight="fill"
            color={colors2.redText}
          />
          <UI_HELPTEXT>Enter a custom separator</UI_HELPTEXT>
        </StyledErrorMessage>
      )}
    </div>
  </Fragment>
);

const TableDirectImportFieldSelector = (props) => {
  const {
    id,
    value,
    onChange,
    options,
    disabled = false,
    icon,
    className,
    style,
    lightboxStylesOverride,
    // Render-prop: the parent owns the trigger markup and receives the state it
    // needs to draw it — `open` for the caret, plus the values derived from
    // `reorderedOptions` in here so the parent doesn't have to re-derive them.
    children,
    // Which panel controls to render (see CONTROL_SELECTOR_TYPES). Omit to show
    // all of them — the full import-config panel.
    controlsToShow,
    // Position the panel against this element instead of the trigger. The
    // trigger still opens it; only the anchoring moves. Omit to anchor to the
    // trigger as usual. Must be an element in state, not a ref (see Lightbox).
    anchorEl,
    // Where the panel opens relative to the trigger (floating-ui placement).
    // Defaults to "bottom-start" (the table column selectors — the panel's left
    // edge lines up with the column's); the Verify Mapping modal passes
    // "right-start" to open beside the row.
    placement = "bottom-start",
    // Keeps the panel inside this element as the table scrolls, instead of
    // letting it travel to the viewport edge with its column. The import sheet
    // passes its own container, so the panel stops at the sheet's edge.
    shiftBoundary,
    usedTagsMap = {},
    titleFieldInfo = { isUsed: false },
    currentFieldName,
    // Reimport-only: lets a (new) column be mapped onto an existing dataset
    // field instead of creating a new one. `existingFields` is the mappable
    // target list (from the BE); `usedExistingTargetsMap` is targetValuePath ->
    // claiming column's sourceValuePath (locked auto-matches + other mappings).
    isImportMoreDataMode = false,
    existingFields = [],
    usedExistingTargetsMap = {},
    // Close the panel as soon as a field TYPE is picked — for consumers where
    // the pick IS the terminal action (the listing-header type-change pencil).
    // The import flow omits it: picking a type there is one edit among many.
    closePanelOnCompChange = false,
    // confirm betton in dropdwn
    renderFieldTypeFooter, 
    onOpenChange: onOpenChangeProp,
  } = props;
  const TAGS_INPUT_COMPS = ["TagsInputSingle", "TagsInputMulti"];
  const MULTI_SELECT_COMPS = ["CheckboxList", "DropdownMulti", "TagsInputMulti"];
  // Comps whose `.value` is indexed in ES as a `keyword` (single un-tokenized
  // term, ~32766-byte limit). Picking one for a column that holds long-text
  // values (e.g. a meeting transcript) risks overflowing that limit and
  // breaking the elastic-sync job, so we disable them when the BE flags the
  // column as containing a keyword-unsafe value (`hasKeywordUnsafeValue`).
  const LONG_TEXT_INCOMPATIBLE_COMPS = [
    "DropdownSingle",
    "DropdownMulti",
    "CheckboxList",
    "RadioList",
    "TagsInputSingle",
    "TagsInputMulti",
  ];
  const columnHasKeywordUnsafeValue = value?.hasKeywordUnsafeValue === true;

  // Reorder options and structure into groups with separators.
  //
  // Membership is the source of truth: every option in `options` (which is already filtered
  // upstream by the BE compsAvailableForDirectDataImport whitelist) appears in some group.
  //
  // Group placement comes from each option's `directImportGroupsIdx` tuple
  // [groupIdx, orderInGroup]. Options without one default to group 3 (end), no specific order.
  // The "title" sentinel option is special-cased to group 1.
  const fieldTypeOptionsWithGroups = useMemo(() => {
    const groupBuckets = {};
    for (const opt of options) {
      let groupIdx;
      let orderInGroup;
      if (opt.value === "title") {
        [groupIdx, orderInGroup] = [1, 1];
      } else if (Array.isArray(opt.directImportGroupsIdx)) {
        [groupIdx, orderInGroup] = opt.directImportGroupsIdx;
      } else {
        // Fallback: any option without an explicit group goes to group 3, no order
        [groupIdx, orderInGroup] = [3, Number.MAX_SAFE_INTEGER];
      }
      if (!groupBuckets[groupIdx]) groupBuckets[groupIdx] = [];
      groupBuckets[groupIdx].push({
        ...opt,
        // An option may bring its own tooltip (e.g. the listing pencil appends
        // why a type is flagged); otherwise it gets the field type's own.
        tooltip: opt.tooltip ?? fieldTypeTooltips[opt.value],
        _orderInGroup: orderInGroup,
      });
    }

    // Sort by groupIdx, then by orderInGroup within each group
    return Object.keys(groupBuckets)
      .map(Number)
      .sort((a, b) => a - b)
      .map((groupIdx) => ({
        groupLabel: "",
        options: groupBuckets[groupIdx]
          .sort((a, b) => a._orderInGroup - b._orderInGroup)
          .map(({ _orderInGroup, ...rest }) => rest),
      }));
  }, [options]);

  // Flat version for finding options (used in useMemo dependencies)
  const reorderedOptions = useMemo(() => {
    return fieldTypeOptionsWithGroups.flatMap((group) => group.options);
  }, [fieldTypeOptionsWithGroups]);

  const allTpls = useGetQueryData("allTpls");
  const platformConfigs = useGetQueryData("platformConfigs");

  // System taxonomy content types (themes / subThemes / tags) are managed by
  // the platform, not as user datasets, so they must never be offered as an
  // "existing tag category" to import into. The BE flags them reserved via
  // SELF_MANAGED_BASE_CONFIGS.reservedContentTypeNames; mirror that list here
  // (lowercased) to filter them out of the tag-category dropdown below.
  const reservedContentTypeNames = useMemo(() => {
    const names =
      platformConfigs?.SELF_MANAGED_BASE_CONFIGS?.reservedContentTypeNames || [];
    return new Set(names.map((n) => n.toLowerCase()));
  }, [platformConfigs]);

  // Derive all field config values directly from value prop (no internal state)
  const enabled = value?.enabled ?? true;
  const comp = value?.dummyCompNameValue === "title" ? "title" : value?.comp;
  const tagType = value?.tagType;

  // Date-format options for the "Date format" dropdown, sourced from the backend
  // (single source of truth: dateFnUtilities.SUPPORTED_DATE_FORMATS, served via
  // getAllPlatformConfigs). Each `value` is a real parser format key, so the user's
  // pick is authoritative at import — no format drift between FE and BE.
  //
  // Filtered by column type: Date (DatePicker) shows date-only formats, Date & Time
  // (DateTimePicker) shows date+time formats. ISO (hasTime === null) appears in both.
  //
  // NOTE: must stay BELOW `comp` — it's in this memo's dependency array, and that array
  // is evaluated on every render, so declaring this above `comp` throws a temporal-dead-zone
  // ReferenceError ("Cannot access 'comp' before initialization").
  const dateFormatOptions = useMemo(() => {
    const wantTime = comp === "DateTimePicker";
    return (platformConfigs?._DirectDataImport?.SUPPORTED_DATE_FORMATS || [])
      .filter((f) => f.hasTime === null || !!f.hasTime === wantTime)
      .map(({ value, example }) => ({
        value,
        display: example ? `${value}  (${example})` : value,
      }));
  }, [platformConfigs, comp]);
  // splitCharacterType stores the format type (comma, semicolon, custom, none)
  // splitCharacter stores the actual character
  const separationFormat =
    value?.splitCharacterType ||
    getSeparationFormatOption(value?.splitCharacter)?.value ||
    "none";
  const separationFormatValue = value?.splitCharacter || "";
  // Reimport "map to existing field" state. In "existing" mode the column adopts
  // the mapped field's type, so the field-type selector is replaced by an
  // "Assign to existing field" dropdown and `effectiveComp` (the mapped field's
  // comp) drives whether the separation-format control is shown.
  const mappingMode = value?.mappingMode || "new";
  const mappedField = value?.mappedField;
  // Auto-matched on reimport: the BE already resolved this column to a stored
  // field, so NEITHER toggle position means anything — "New Field" is inert
  // (the column keeps its stored targetValuePath either way) and "Existing
  // Field" onto its own field is a no-op. Showing the toggle defaulted to "New
  // Field" (mappingMode falls back to "new" when nothing set it) read as "this
  // will create a duplicate column" and pushed users into re-mapping a column
  // that was already correct. State the connection instead.
  const isAlreadyMatched =
    isImportMoreDataMode && value?.isExistingTemplateField === true;
  const matchedFieldLabel = isAlreadyMatched
    ? existingFields.find((f) => f.targetValuePath === value?.targetValuePath)
        ?.label
    : undefined;
  // Header pill: this column will CREATE a field in the dataset. True only on a
  // reimport (on a first import every column is new, so the pill would be
  // noise), and only while the column is still headed for a new field — once
  // the user assigns it to an existing one the pill clears, and the "Assign to
  // existing field" dropdown is itself the record of where the data goes. A
  // column excluded from the import creates nothing, so it doesn't earn one.
  const showNewPill =
    isImportMoreDataMode &&
    enabled &&
    !isAlreadyMatched &&
    !(mappingMode === "existing" && mappedField);
  const effectiveComp = mappingMode === "existing" ? mappedField?.comp : comp;
  // The item whose field-type identity drives the UI: the mapped existing
  // field in "existing" mode (its entry carries a minimal props subset from
  // the BE — shortText/annotations), else the column itself.
  const effectiveItem = mappingMode === "existing" ? mappedField : value;
  const effectiveFieldTypeKey = deriveFieldTypeKey(
    effectiveItem,
    reorderedOptions
  );
  // When mapped, the header should show the existing field's type icon, not
  // the source column's auto-detected one. (Short/Long resolve via the
  // derived key — both are LexicalTextEditor.)
  const effectiveIcon =
    mappingMode === "existing" && mappedField
      ? reorderedOptions.find((o) => o.value === effectiveFieldTypeKey)?.icon ||
        icon
      : icon;

  // "Include in AI Analysis" — shown on every text column. Short Text
  // defaults OFF (detection preset ships annotations.disable), Long Text
  // defaults ON; the toggle flips props.annotations.disable in the parent
  // reducer and never touches the comp. Also shown (locked, informative) for
  // legacy TextInput fields on reimport. Locked whenever the column is tied
  // to an existing field.
  const isLexicalBacked =
    effectiveComp === "LexicalTextEditor" ||
    LEGACY_RICH_TEXT_COMPS.includes(effectiveComp);
  const showAiToggle = isLexicalBacked || effectiveComp === "TextInput";
  const aiAnalysisOn =
    isLexicalBacked && isAiAnalysisOnForOutlineItem(effectiveItem);
  const aiToggleLocked =
    isImportMoreDataMode &&
    (value?.isExistingTemplateField === true || mappingMode === "existing");

  // Low-confidence detection dot — shown until the user opens this column's
  // picker (typeConfidenceAcked). Existing/locked reimport fields carry no
  // detectionConfidence (nothing was detected for them), so they never dot.
  const showConfidenceDot =
    enabled &&
    !disabled &&
    value?.detectionConfidence === "low" &&
    value?.typeConfidenceAcked !== true;

  // `controlsToShow` narrows the panel down to the named controls. Undefined
  // means show all, so consumers that don't pass it (DirectDataImport) are
  // unaffected.
  const showControl = (name) => !controlsToShow || controlsToShow.includes(name);

  const [open, setOpen] = useState(false);

  // "Existing Field" with nothing assigned yet is an unfinished choice: the
  // column would import into no field at all. Until it's resolved (a field
  // picked, or the toggle flipped back to "New Field") the rest of the page is
  // blocked, and reaching for anything outside the panel highlights the
  // dropdown rather than doing what was clicked.
  const needsExistingFieldChoice =
    mappingMode === "existing" && !mappedField?.targetValuePath;
  const [highlightMappingRequired, setHighlightMappingRequired] =
    useState(false);

  // Resolved — drop the highlight so it can't linger into the next open.
  useEffect(() => {
    if (!needsExistingFieldChoice) setHighlightMappingRequired(false);
  }, [needsExistingFieldChoice]);

  // Header shows the field TYPE — the existing field's type when mapped,
  // otherwise the column's own. Options are keyed (Short Text / Long Text
  // share comp LexicalTextEditor), so resolution goes through the derived
  // field-type key; the raw comp still drives behavior + what's submitted.
  const selectedOption = useMemo(() => {
    return reorderedOptions.find((op) => op.value === effectiveFieldTypeKey);
  }, [reorderedOptions, effectiveFieldTypeKey]);

  // Anchoring, viewport clamping, repositioning on scroll/column-resize, and
  // click-outside dismissal are all handled by the shared Lightbox (floating-ui
  // `autoUpdate` + `shift` + `useDismiss`), replacing the hand-rolled rAF loop.

  // Reset separation format when lightbox closes if custom is selected but empty
  useEffect(() => {
    if (!open && separationFormat === "custom" && !separationFormatValue) {
      onChange(null, {
        separationFormat: "none",
        separationFormatValue: "",
      });
    }
  }, [open]);

  return (
    <div className={className} style={style}>
      {/* Left-aligned to the trigger; crossAxisOffset={0} because the shared
          default of 2 would sit the panel 2px past the column's left edge.
          `shift` keeps it inside `shiftBoundary` when given (the import sheet)
          and inside the viewport otherwise, so a column near an edge doesn't
          push the panel off the surface.
          `enabled={!disabled}` replaces the old in-onClick disabled guard: when a
          field already exists in the template on a reimport, its config is locked
          and the trigger must not open the editor. */}
      <Lightbox
        open={open}
        onOpenChange={(nextOpen) => {
          // Opening the picker counts as verifying a low-confidence detection:
          // clear the dot permanently for this column. (Was in the trigger's
          // onClick before the shared-Lightbox migration; onOpenChange is the
          // equivalent hook now.)
          if (nextOpen && showConfidenceDot) {
            onChange(null, { typeConfidenceAcked: true });
          }
          // Outside-press and Escape both route through here. Neither may
          // abandon a half-made mapping — point at the dropdown instead.
          if (!nextOpen && needsExistingFieldChoice) {
            setHighlightMappingRequired(true);
            return;
          }
          setOpen(nextOpen);
          onOpenChangeProp?.(nextOpen);
        }}
        enabled={!disabled}
        placement={placement}
        crossAxisOffset={0}
        anchorEl={anchorEl}
        shiftBoundary={shiftBoundary}
      >
        {open &&
          needsExistingFieldChoice &&
          createPortal(
            <StyledMappingBlockingOverlay
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setHighlightMappingRequired(true);
              }}
            />,
            document.body
          )}
        <LightboxTrigger asChild>
          {children({
            open,
            effectiveIcon,
            selectedOption,
            enabled,
            showConfidenceDot,
            // For the trigger's sparkle indicator: text column included in
            // AI analysis (Short/Long are both Lexical; short defaults off).
            isLexicalBacked,
            aiAnalysisOn,
            showNewPill,
          })}
        </LightboxTrigger>
        {/* trapFocus off: this panel is a form (text inputs, dropdowns), not a
            menu — trapping would steal focus to the first row on open and fight
            the inputs. Same call the other input-bearing panels make. */}
        <LightboxContent
          trapFocus={false}
          className={"StyledLightbox_styleOverrideHandle"}
          style={{
            zIndex: 999999999,
            width: `${LIGHTBOX_WIDTH}px`,
            overflow: "hidden",
            ...lightboxStylesOverride,
          }}
        >
          {/* The lightbox renders through a FloatingPortal, i.e. outside the
              TypoV2ClassesProvider that OolibThemeProvider wraps the app in. Any
              oolib comp that styles itself via a `UI_*` className rather than a
              styled-component (the dropdown's search TextInput, for one) loses
              its type scale out here, so re-provide the classes inside. */}
          <TypoV2ClassesProvider style={{ display: "contents" }}>
          <StyledPanelSections>
            <StyledLightboxSection>
              <UI_BODY_SEMIBOLD_SM_DF>
                {currentFieldName ||
                  value?.explicitBlockConfig?.label ||
                  value?.sourceValuePath}
              </UI_BODY_SEMIBOLD_SM_DF>
            </StyledLightboxSection>
          {/* One DOM node, so the whole config block is a single section —
              a Fragment here would make each control its own section. */}
          {enabled && (
            <div>
              {/* Unrecognised column: create a brand-new field, or map it onto
                  an existing dataset field.

                  An auto-matched column shows the same bar so every column's
                  panel has the same shape, but READ-ONLY, parked on the truth:
                  it already feeds a stored field. Neither position is live,
                  because neither works. "New Field" would need a fresh field
                  name minted for the column, which only the BE can do (and must
                  do before any batch runs, or batch 1 mints a different name
                  than batch 0 and splits the column across two fields).
                  "Existing Field" sets mappingMode, which swaps this panel for
                  the assign-to dropdown — hiding the Field type control the
                  column still needs, and blocking submit via validateMappings
                  until a field is picked. Inert beats misleading. With no
                  existing fields there's nothing to assign to, so no toggle. */}
              {isImportMoreDataMode && existingFields.length > 0 && showControl("mappingMode") && (
                <StyledSegmentedToggleWrapper>
                  <SegmentedControl
                    style={
                      isAlreadyMatched
                        ? { pointerEvents: "none", opacity: 0.6 }
                        : undefined
                    }
                    options={[
                      { display: "New Field", value: "new" },
                      { display: "Existing Field", value: "existing" },
                    ]}
                    value={isAlreadyMatched ? "existing" : mappingMode}
                    onChange={(v) => {
                      // Belt and braces: pointer-events already blocks this.
                      if (isAlreadyMatched) return;
                      // No seeding: the target starts empty and the user must
                      // pick one. Switching back to "New Field" is always
                      // allowed — it's the way out of the pending state.
                      onChange(null, {
                        mappingMode: v === "existing" ? "existing" : "new",
                      });
                    }}
                  />
                </StyledSegmentedToggleWrapper>
              )}

              {mappingMode === "existing" ? (
                /* ---- Map this column onto an existing dataset field ---- */
                <StyledLightboxSection>
                  <DropdownSingle
                    popOutOfOverflowHiddenParent
                    S
                    isSearchable
                    label="Assign to existing field"
                    value={mappedField?.targetValuePath}
                    options={[
                      {
                        groupLabel: "",
                        options: existingFields.map((f) => {
                          const isUsedByOtherField =
                            usedExistingTargetsMap[f.targetValuePath] &&
                            usedExistingTargetsMap[f.targetValuePath] !==
                              value.sourceValuePath;
                          return {
                            display: f.label,
                            value: f.targetValuePath,
                            icon: reorderedOptions.find(
                              (o) =>
                                o.value ===
                                deriveFieldTypeKey(f, reorderedOptions)
                            )?.icon,
                            disabled: isUsedByOtherField,
                            desc: isUsedByOtherField
                              ? "Already mapped from another column"
                              : undefined,
                          };
                        }),
                      },
                    ]}
                    saveValueAsString
                    onChange={(_, vp) => {
                      const F = existingFields.find(
                        (f) => f.targetValuePath === vp
                      );
                      if (F) onChange(null, { mappedExistingField: F });
                    }}
                  />
                  {highlightMappingRequired && (
                    <StyledErrorMessage>
                      <DisplayIcon
                        size={12}
                        icon="WarningDiamond"
                        weight="fill"
                        color={colors2.redText}
                      />
                      <UI_HELPTEXT>
                        Choose the field this column maps to, or switch back to
                        New Field
                      </UI_HELPTEXT>
                    </StyledErrorMessage>
                  )}
                  {MULTI_SELECT_COMPS.includes(effectiveComp) && (
                    <MultiSelectSeparationConfig
                      separationFormat={separationFormat}
                      separationFormatValue={separationFormatValue}
                      disabled={disabled}
                      onChange={onChange}
                    />
                  )}
                  {/* Mapped onto an existing text field: AI analysis state is
                      the field's own (short = off, long = on, per the props
                      subset the BE ships on existingFields) — always locked. */}
                  {showControl("aiAnalysis") && showAiToggle && (
                    <FieldSelectorListItem
                      icon={"Sparkle"}
                      display={
                        SM_UIContent.SM_DirectDataImport.dropdowns
                          .includeInAiAnalysis
                      }
                      isActive={aiAnalysisOn}
                      disabled
                      onChange={() => {}}
                    />
                  )}
                </StyledLightboxSection>
              ) : (
              /* ---- Configure a brand-new field ---- */
              <Fragment>
              {showControl("changeFieldComp") && (
              <StyledLightboxSection>
                <DropdownSingle
                  label="Field type"
                  tooltip={fieldTypeTooltipConfig}
                  disabled={disabled}
                  saveValueAsString={true}
                  popOutOfOverflowHiddenParent
                  S
                  // Option values are field-type KEYS (Short Text and Long
                  // Text share comp LexicalTextEditor); the raw comp is
                  // maintained by the parent onChange reducer.
                  value={effectiveFieldTypeKey}
                  options={fieldTypeOptionsWithGroups.map((group) => ({
                    groupLabel: group.groupLabel,
                    options: group.options.map((option) => {
                      // Disable "title" option if it's already used by another field
                      if (option.value === "title") {
                        const isTitleUsedByOtherField =
                          titleFieldInfo.isUsed &&
                          titleFieldInfo.fieldName !== currentFieldName;
                        return {
                          ...option,
                          disabled: isTitleUsedByOtherField,
                          desc: isTitleUsedByOtherField
                            ? `Already used by field (${titleFieldInfo.fieldName})`
                            : undefined,
                        };
                      }

                      // Disable keyword-`value` comps when the column holds
                      // a long-text value — picking one risks overflowing
                      // the ES keyword term limit and breaking the sync.
                      // Compared via option.comp (option.value is a KEY);
                      // never disable the currently-selected option, so an
                      // existing/locked field never renders an empty selector.
                      if (
                        columnHasKeywordUnsafeValue &&
                        LONG_TEXT_INCOMPATIBLE_COMPS.includes(option.comp) &&
                        option.value !== effectiveFieldTypeKey
                      ) {
                        return {
                          ...option,
                          disabled: true,
                          desc: "Not available for columns with very long values",
                        };
                      }
                      return option;
                    }),
                  }))}
                  // popOutOfOverflowHiddenParent
                  onChange={(_, v) => {
                    // v is a field-type KEY — resolve the option to reason
                    // about its comp.
                    const selectedOpt = reorderedOptions.find(
                      (o) => o.value === v
                    );
                    const isMultiSelectField = MULTI_SELECT_COMPS.includes(
                      selectedOpt?.comp
                    );

                    onChange(null, {
                      fieldTypeKey: v,
                      // `comp` rides along for consumers that work purely in
                      // comp space (the listing-header ChangeFieldCompDropdown
                      // → PATCH changeFieldComp). The import flow ignores it
                      // and keys off fieldTypeKey.
                      comp: selectedOpt?.comp,
                      // Clear tagType when changing type (user must re-select)
                      tagType: undefined,
                      // For multi-select fields, ensure separation format exists
                      ...(isMultiSelectField && {
                        separationFormat: separationFormat || "none",
                        separationFormatValue: separationFormatValue || "",
                      }),
                      // Clear separation format for non-multi-select fields
                      ...(!isMultiSelectField && {
                        separationFormat: undefined,
                        separationFormatValue: undefined,
                      }),
                    });

                    if (closePanelOnCompChange) setOpen(false);
                  }}
                />
                {/* Reimport, column already resolved to a stored field. Sits
                    under Field type because that's the control it qualifies:
                    the type shown is the EXISTING field's, and the column's
                    data joins that field rather than starting a new one. */}
                {isAlreadyMatched && (
                  <WarningNotice style={{ marginTop: "0.4rem" }}>
                    {matchedFieldLabel
                      ? `This column is already connected to "${matchedFieldLabel}". Its data will be added to that existing field.`
                      : "This column is already connected to an existing field. Its data will be added to that existing field."}
                  </WarningNotice>
                )}
                {showControl("separationFormat") &&
                  MULTI_SELECT_COMPS.includes(comp) && (
                    <MultiSelectSeparationConfig
                      separationFormat={separationFormat}
                      separationFormatValue={separationFormatValue}
                      disabled={disabled}
                      onChange={onChange}
                    />
                  )}
                {renderFieldTypeFooter?.({ closePanel: () => setOpen(false) })}
              </StyledLightboxSection>
              )}
              {/* All text columns: include/exclude from AI analysis — flips
                  props.annotations.disable on the item (comp never changes;
                  Short and Long Text are both Lexical). Locked for fields
                  that already exist in the template on reimport. */}
              {showControl("aiAnalysis") && showAiToggle && (
                <StyledLightboxSection>
                  <FieldSelectorListItem
                    icon={"Sparkle"}
                    display={
                      SM_UIContent.SM_DirectDataImport.dropdowns
                        .includeInAiAnalysis
                    }
                    isActive={aiAnalysisOn}
                    disabled={aiToggleLocked}
                    onChange={() => {
                      onChange(null, {
                        includeInAiAnalysis: !aiAnalysisOn,
                      });
                    }}
                  />
                </StyledLightboxSection>
              )}
              {/* Date / Date & Time columns: the user picks the exact format their
                  values are in, so the backend parses with a known format instead of
                  guessing. Pre-filled from auto-detection when available; required
                  otherwise. The options list is filtered to date-only vs date+time
                  based on the column type (see dateFormatOptions). */}
              {showControl("dateFormat") &&
                ["DatePicker", "DateTimePicker"].includes(comp) && (
                <StyledLightboxSection>
                  <DropdownSingle
                    S
                    label="Date format"
                    placeholder="Select the format your dates are in"
                    disabled={disabled}
                    saveValueAsString
                    popOutOfOverflowHiddenParent
                    value={value?.detectedDateFormat}
                    options={[{ groupLabel: "", options: dateFormatOptions }]}
                    onChange={(_, v) =>
                      onChange(null, { detectedDateFormat: v })
                    }
                  />
                  {!value?.detectedDateFormat && (
                    <StyledErrorMessage>
                      <DisplayIcon
                        size={12}
                        icon="WarningDiamond"
                        weight="fill"
                        color={colors2.redText}
                      />
                      <UI_HELPTEXT>
                        Select the date format to import this column correctly
                      </UI_HELPTEXT>
                    </StyledErrorMessage>
                  )}
                </StyledLightboxSection>
              )}
              {showControl("tagType") && TAGS_INPUT_COMPS.includes(comp) && (
                <StyledLightboxSection>
                  <DropdownSingle
                    popOutOfOverflowHiddenParent
                    S
                    isSearchable
                    label={SM_UIContent.SM_DirectDataImport.dropdowns.tags}
                    value={tagType}
                    options={allTpls
                      .filter(
                        (tpl) =>
                          ["collections", "publishing"].includes(
                            tpl?.general?.segment
                          ) &&
                          !reservedContentTypeNames.has(
                            tpl.kp_content_type?.toLowerCase()
                          )
                      )
                      .map((tpl) => {
                        const tplTagType = tpl.kp_content_type;
                        const isUsedByOtherField =
                          usedTagsMap[tplTagType] &&
                          usedTagsMap[tplTagType] !== currentFieldName;
                        return {
                          ...tpl,
                          display:
                            tpl.general?.content?.title ||
                            tpl.kp_content_type,
                          value: tplTagType,
                          disabled: isUsedByOtherField,
                          desc: isUsedByOtherField
                            ? `Already connected to a field (${usedTagsMap[tplTagType]})`
                            : undefined,
                        };
                      })}
                    saveValueAsString
                    onChange={(_, v) => {
                      onChange(null, { tagType: v });
                    }}
                  />
                </StyledLightboxSection>
              )}
              </Fragment>
              )}
            </div>
          )}
          {showControl("dontImport") && (
              <StyledLightboxSection>
                <FieldSelectorListItem
                  icon={"X"}
                  color={colors2.redText}
                  display={
                    SM_UIContent.SM_DirectDataImport.dropdowns.dontImport
                  }
                  isActive={!enabled}
                  onChange={() => {
                    onChange(null, { enabled: !enabled });
                  }}
                />
              </StyledLightboxSection>
          )}
          </StyledPanelSections>
          </TypoV2ClassesProvider>
        </LightboxContent>
      </Lightbox>
    </div>
  );
};

export default TableDirectImportFieldSelector;
